import { Helmet } from 'react-helmet-async';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/_default/lib/utils/get-variant-value';

// Default OG image for social sharing (dumpster/waste management themed)
// This should be a 1200x630 image hosted in the public assets
const DEFAULT_OG_IMAGE = '/og-default.png';

// Common keywords for waste removal and dumpster rental businesses
const COMMON_KEYWORDS = [
  'dumpster rental',
  'waste removal',
  'junk removal',
  'construction debris removal',
  'roll off dumpster',
  'same day dumpster delivery',
  'affordable waste removal',
  'commercial waste removal',
  'residential waste removal',
  'garbage removal',
  'trash removal',
  'recycling services',
  'waste management',
  'construction waste disposal',
  'demolition debris removal',
];

const getImageMimeType = (imageUrl: string): string | undefined => {
  const pathname = (() => {
    try {
      return new URL(imageUrl, 'https://sitesapp.local').pathname;
    } catch {
      return imageUrl.split(/[?#]/, 1)[0] ?? imageUrl;
    }
  })().toLowerCase();

  if (pathname.endsWith('.svg')) return 'image/svg+xml';
  if (pathname.endsWith('.png')) return 'image/png';
  if (pathname.endsWith('.ico')) return 'image/x-icon';
  return undefined;
};

interface GeoLocation {
  city?: string;
  region?: string; // State
  country?: string;
  latitude?: number;
  longitude?: number;
}

interface BaseSEOProps {
  title: string | string[];
  description: string | string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  name?: string;
  noindex?: boolean;
  keywords?: string[];
  structuredData?: Record<string, unknown>;
  /** Page type for og:type - defaults to 'website' */
  pageType?: 'website' | 'article' | 'product' | 'local_business';
  /** Geo location for local SEO */
  geoLocation?: GeoLocation;
  /** Theme color for mobile browsers */
  themeColor?: string;
  /** Author name for article pages */
  author?: string;
  /** Published date for article pages (ISO 8601 format) */
  publishedTime?: string;
  /** Modified date for article pages (ISO 8601 format) */
  modifiedTime?: string;
}

/**
 * Enhanced BaseSEO component with comprehensive meta tags for optimal search engine visibility.
 * Includes support for:
 * - Basic meta tags (title, description, keywords)
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Geo tags for local SEO
 * - Schema.org structured data (LocalBusiness by default)
 * - Performance hints (preconnect)
 * - Mobile web app configuration
 */
export const BaseSEO = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogImageAlt,
  name,
  noindex,
  keywords,
  structuredData,
  pageType = 'website',
  geoLocation,
  themeColor,
  author,
  publishedTime,
  modifiedTime,
}: BaseSEOProps) => {
  const siteName = name || 'Dumpster Rental Services';
  const finalTitle = Array.isArray(title) ? title[0] : title;
  const finalDescription = Array.isArray(description)
    ? description[0]
    : description;
  const finalKeywords = Array.isArray(keywords) ? keywords : COMMON_KEYWORDS;

  // Get customer-specific values from variants
  const faviconUrl = useVariantValue<string>('FAVICON_IMAGE', '/favicon.svg');
  const logoUrl = useVariantValue<string>('LOGO_IMAGE', '');
  const faviconType = getImageMimeType(faviconUrl);
  const facebookUrl = useVariantValue('FACEBOOK_LINK', '');
  const twitterUrl = useVariantValue('X_LINK', '');
  const seoTitle = useVariantValue('SEO_TITLE', finalTitle);
  const seoDescription = useVariantValue('SEO_DESCRIPTION', finalDescription);
  const seoKeywords = useVariantValue('SEO_KEYWORDS', finalKeywords.join(', '));

  // Use theme color from props or default
  const finalThemeColor = themeColor || '#3463F8';

  // Generate social media links array
  const socialLinks = [facebookUrl, twitterUrl].filter(Boolean);

  // Get current URL for meta tags
  const currentUrl =
    canonicalUrl ||
    (typeof window !== 'undefined' ? window.location.href : '');
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : '';

  // Build final OG image URL (ensure absolute URL)
  // Priority: 1. Explicit ogImage prop, 2. Logo from variant, 3. Default OG image
  const ogImageSource = ogImage || logoUrl || DEFAULT_OG_IMAGE;
  const finalOgImage = ogImageSource
    ? ogImageSource.startsWith('http')
      ? ogImageSource
      : `${baseUrl}${ogImageSource}`
    : `${baseUrl}${DEFAULT_OG_IMAGE}`;
  const finalLogo = logoUrl
    ? logoUrl.startsWith('http')
      ? logoUrl
      : `${baseUrl}${logoUrl}`
    : undefined;

  // Default structured data with enhanced LocalBusiness schema
  const defaultStructuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'WasteManagementCompany'],
    '@id': `${baseUrl}/#organization`,
    name: siteName,
    image: finalOgImage,
    ...(finalLogo && {
      logo: {
        '@type': 'ImageObject',
        url: finalLogo,
      },
    }),
    description: seoDescription,
    url: baseUrl,
    priceRange: '$$',
  };

  // Add address if geo location provided
  if (geoLocation?.city) {
    defaultStructuredData.address = {
      '@type': 'PostalAddress',
      addressLocality: geoLocation.city,
      ...(geoLocation.region && { addressRegion: geoLocation.region }),
      addressCountry: geoLocation.country || 'US',
    };
  }

  // Add geo coordinates if provided
  if (geoLocation?.latitude && geoLocation?.longitude) {
    defaultStructuredData.geo = {
      '@type': 'GeoCoordinates',
      latitude: geoLocation.latitude,
      longitude: geoLocation.longitude,
    };
  }

  // Add social links if available
  if (socialLinks.length > 0) {
    defaultStructuredData.sameAs = socialLinks;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <link rel="shortcut icon" href={faviconUrl} />
      {faviconType ? (
        <link rel="icon" type={faviconType} href={faviconUrl} />
      ) : (
        <link rel="icon" href={faviconUrl} />
      )}
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta charSet="utf-8" />

      {/* Theme Color for mobile browsers */}
      <meta name="theme-color" content={finalThemeColor} />
      <meta name="msapplication-TileColor" content={finalThemeColor} />

      {/* Mobile Web App Configuration */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={ogImageAlt || seoTitle} />

      {/* Article-specific Open Graph tags */}
      {pageType === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:image:alt" content={ogImageAlt || seoTitle} />

      {/* Geo Tags for Local SEO */}
      {geoLocation?.city && geoLocation?.region && (
        <>
          <meta
            name="geo.placename"
            content={`${geoLocation.city}, ${geoLocation.region}`}
          />
          <meta
            name="geo.region"
            content={`${geoLocation.country || 'US'}-${geoLocation.region}`}
          />
        </>
      )}
      {geoLocation?.latitude && geoLocation?.longitude && (
        <meta
          name="geo.position"
          content={`${geoLocation.latitude};${geoLocation.longitude}`}
        />
      )}

      {/* Robots Directives */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <>
          <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
          <meta name="googlebot" content="index,follow" />
        </>
      )}

      {/* Performance Hints - Preconnect to common third-party domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};
